import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { AccessList, ListedGroup } from "../../../api/access/accessModel";
import ListCard from "../../../ui/layout/ListCard";
import type { MenuItem } from "../../../ui/overlays/MenuButton";
import type { AccessChanges } from "../changes/useAccessChanges";
import { depthOf, isMovable, moveAbove } from "../model/groupOrder";
import type { GroupNamer } from "../model/groupNames";
import { groupMenuSections, moveGroup } from "./groupMenuSections";
import GroupListRow from "./GroupListRow";

/** The drag's payload type: a group ID, which other pages ignore. */
const GROUP_DRAG_TYPE = "application/x-dmw-group";

interface GroupListProps {
  list: AccessList;
  namer: GroupNamer;
  /** Where a group's own screen is. */
  groupPath: (groupId: string) => string;
  changes: AccessChanges;
  /** "New group", for those who manage the list. */
  action?: ReactNode;
  /** Further entries of a group's menu, such as removing it. */
  menuItems?: (group: ListedGroup) => readonly MenuItem[];
}

/**
 * A scope's groups, first on top (accounts.md GS-1, GS-3), with the one rule line. A group
 * the viewer manages moves by one drag, or by "Move above" in its menu; the whole new order
 * goes to the server with the revision it was read at, and Undo puts it back.
 */
export default function GroupList({ list, namer, groupPath, changes, action, menuItems }: GroupListProps) {
  const { t } = useTranslation();
  const [moving, setMoving] = useState<string | null>(null);
  const [dropAbove, setDropAbove] = useState<string | null>(null);

  return (
    <ListCard title={t("access.groups")} actions={action}>
      <p className="app-status-info border-x-0 px-4 py-2 text-xs">{t("access.rule")}</p>
      <ul className="app-divide-y">
        {list.groups.map((group) => {
          const parent = group.parentId === null ? undefined : list.groups.find((listed) => listed.groupId === group.parentId);
          return (
            <GroupListRow
              key={group.groupId}
              group={group}
              projectId={list.projectId}
              itemKinds={list.itemKinds}
              name={namer.name(group.groupId)}
              parentName={parent === undefined || list.projectId !== null ? null : namer.name(parent.groupId)}
              depth={list.projectId === null ? depthOf(group, list.groups) : 0}
              href={groupPath(group.groupId)}
              busy={changes.busy}
              dropMarked={dropAbove === group.groupId}
              menuSections={groupMenuSections(t, list, group.groupId, namer, changes, menuItems?.(group) ?? [])}
              drag={isMovable(group) ? {
                type: GROUP_DRAG_TYPE,
                onStart: () => { setMoving(group.groupId); },
                onEnd: () => { setMoving(null); setDropAbove(null); },
              } : undefined}
              onDragOver={(event) => {
                if (moving === null || moveAbove(list.groups, moving, group.groupId) === null) return;
                event.preventDefault();
                setDropAbove(group.groupId);
              }}
              onDrop={(event) => {
                event.preventDefault();
                const movingId = event.dataTransfer.getData(GROUP_DRAG_TYPE);
                setMoving(null);
                setDropAbove(null);
                if (movingId !== "") moveGroup(t, list, namer, changes, movingId, group.groupId);
              }}
            />
          );
        })}
      </ul>
    </ListCard>
  );
}
